/**
 * Help & Support content for the construction tenant — the monthly supplier
 * file cycle, spend leakage, division scoring, contracts, lead times, audits
 * and risk, written for the way that customer actually runs its suppliers.
 * Selected in `pages/Help.tsx` when the `supplier_uploads` feature (the
 * construction cycle) is on; other tenants keep the platform FAQ.
 *
 * `slug` keys line up with `PlatformSettings.help_layout.faq`, so a superuser
 * can hide a whole category for the tenant.
 */
export type FaqAudience = 'customer' | 'supplier' | 'both';
export interface FaqItem { question: string; answer: string; audience: FaqAudience }
export interface FaqCategory { slug: string; category: string; items: FaqItem[] }

export const CONSTRUCTION_FAQ: FaqCategory[] = [
  {
    slug: 'getting_started',
    category: 'Getting started',
    items: [
      {
        question: 'What does the "View Data For" switcher at the top do?',
        audience: 'customer',
        answer: 'It sets the scope every page reads: the whole group, one region, or one division. Only the companies you have been given access to appear in it. Switch it and the Dashboard, Supplier Performance, Spend Leakage, Purchasing, Suppliers, Audits and the rest re-scope to that selection — a division user sees only their division.',
      },
      {
        question: 'Where do I see notifications?',
        audience: 'both',
        answer: 'The bell at the top right. Reminders, escalations, overdue actions, contract dates, risk reviews and SLA alerts all arrive there. Email delivery is switched off for the moment, so the bell is the place to look.',
      },
      {
        question: 'How do I choose which alerts I get?',
        audience: 'customer',
        answer: 'User Management → open the user → Site Access. The Procurement and Quality ticks decide whether purchasing-type alerts (contracts, price variance) and quality-type alerts reach that person. Alerts addressed to a named owner — the contract owner, an auditor, an action owner — always reach them.',
      },
      {
        question: 'How do I update my profile or change my password?',
        audience: 'both',
        answer: 'My Account in the sidebar. Name, phone and job title are on the Profile tab; the password is under Security; which alerts you receive is under Notifications.',
      },
    ],
  },
  {
    slug: 'dashboard',
    category: 'Dashboard and supplier performance',
    items: [
      {
        question: 'Where do the Dashboard figures come from?',
        audience: 'customer',
        answer: 'Current Performance is built from the monthly Supplier Performance files — every delivery line judged on its dates. Spend is the invoice ledger by the date the invoice was posted. Cost is order values. Pick a month, a 12-week trend or year to date, and click any tile or chart segment to drill into the lines behind it.',
      },
      {
        question: 'What does the Supplier Performance page show?',
        audience: 'customer',
        answer: 'One row per supplier from their monthly files: on time in full (OTIF), delivery against the agreed SLA date, call-off within SLA, delivery against the required date, and quality (NCRs). The target is 95% unless Admin Setup → Performance says otherwise. Suppliers below target for consecutive months are flagged.',
      },
      {
        question: 'Why does the latest month look thin or empty?',
        audience: 'customer',
        answer: 'Files for a month arrive during the following month (due on the 10th), so the newest month fills up gradually. Lines with no dates to judge are counted as "not judged" rather than late.',
      },
      {
        question: 'What do I see on my Dashboard?',
        audience: 'supplier',
        answer: 'Your own delivery and quality figures as your customer sees them — OTIF, on-time delivery, late lines and NCRs — for the latest complete month, per week or year to date, all from the files you uploaded.',
      },
    ],
  },
  {
    slug: 'monthly_uploads',
    category: 'Monthly supplier files',
    items: [
      {
        question: 'What do suppliers upload each month, and when?',
        audience: 'customer',
        answer: 'Two files per month: Supplier Performance (deliveries) and Quality (NCRs). They are due on the 10th of the following month. A reminder goes out three days before, an overdue notice on the day after, and an escalation a week later.',
      },
      {
        question: 'How do I see who has and has not uploaded?',
        audience: 'customer',
        answer: 'Supplier Upload History. Pick the period: the tiles count suppliers due, files in, pending and overdue, and the tabs split suppliers who did upload from those who did not. Send reminder chases one supplier; Chase all chases everyone still missing a file.',
      },
      {
        question: 'What is Data Upload for?',
        audience: 'customer',
        answer: 'Your own monthly Invoice file. It feeds the Spend figures and the spend-leakage calculation. Upload it from Data Upload; rejected rows are listed with the reason so the file can be fixed and re-sent.',
      },
      {
        question: 'How do I upload my monthly files?',
        audience: 'supplier',
        answer: 'Monthly Uploads. Each period shows the two files expected — Supplier Performance and Quality — with their due date. Drop the file on its row; the columns are checked, rejected rows are reported, and the accepted figures (rows, OTIF, SLA %) appear straight away. Replace file swaps a file you have already sent.',
      },
      {
        question: 'What columns must the Supplier Performance file have?',
        audience: 'supplier',
        answer: 'Required: Barratt_PO_Number, Supplier_Internal_Reference, Actual_Date_of_Delivery. Optional: Date_of_Call_Off, Required_Date, Date_of_Agreed_Delivery, Current_Promise_Date, SLA_Delivery_Date, Days_of_Early_Allowed, Days_of_Late_Allowed, Order_Value_line_item, Site_Plot_Number, BDW_Divisional_Reference, and Commodity_Code where a commodity-specific lead time applies. Download the template from the upload row.',
      },
      {
        question: 'How is my OTIF worked out from the file?',
        audience: 'supplier',
        answer: 'A line is measured when it has an agreed date and an actual delivery date. It is on time when the actual date sits inside the early and late allowance around the agreed date. OTIF is on-time lines over measured lines.',
      },
    ],
  },
  {
    slug: 'spend_leakage',
    category: 'Spend leakage',
    items: [
      {
        question: 'What counts as spend leakage?',
        audience: 'customer',
        answer: 'An invoice line whose commodity is not one the supplier is approved for — off-contract or non-preferred spend. Lines with no commodity are shown as unclassified rather than counted either way. The rule is applied per invoice line from the Invoice file.',
      },
      {
        question: 'Why is leakage split by "uploaded" and "did not upload"?',
        audience: 'customer',
        answer: 'So you can see how much of the leakage sits with suppliers who are inside the monthly reporting cycle and how much with those who are not. Suppliers we cannot identify on an invoice line are "not tracked", not accused of missing an upload.',
      },
      {
        question: 'How do I read the running 12-month graph?',
        audience: 'customer',
        answer: 'One bar per complete month: uploaded, not uploaded and untracked spend stacked, with the leakage percentage as a line. A missing bar means no report for that month — nothing was classified — not zero leakage. The tiles headline the newest, longest report; the items page lists every line with search, commodity filter and export.',
      },
      {
        question: 'Does a division see its own leakage?',
        audience: 'customer',
        answer: 'Yes — switch View Data For to the division and the reports, tiles and items are that division\'s invoice lines only.',
      },
    ],
  },
  {
    slug: 'division_scoring',
    category: 'Division performance and score report',
    items: [
      {
        question: 'How does division scoring work?',
        audience: 'customer',
        answer: 'Each quarter every division scores the suppliers it used. Division Performance is where a division enters and edits its scores; the quarter is signed off when complete. The Division Score Report rolls the scores up across divisions per supplier per quarter.',
      },
      {
        question: 'Which suppliers appear for scoring?',
        audience: 'customer',
        answer: 'The suppliers your division works with. Use the supplier filter to find one; scores already entered for the quarter are shown and can be corrected until the quarter is signed off.',
      },
    ],
  },
  {
    slug: 'contracts',
    category: 'Contracts and price variance',
    items: [
      {
        question: 'Where do I record an agreement with a supplier?',
        audience: 'customer',
        answer: 'On the supplier\'s profile, Contracts tab: contract number, type, term, owner, auto-renewal, notice period, reminder days and business-review cadence, with the signed document attached. The Contracts page then lists every agreement across suppliers with its end date, days left, owner and status.',
      },
      {
        question: 'What are pricebooks?',
        audience: 'customer',
        answer: 'The rate card under a contract: part number, unit or lot price, quantity band and validity. Contracts → Pricebooks to add, import from a spreadsheet, or export. The Price Variance page compares invoices against these rates.',
      },
      {
        question: 'What contract reminders do I get?',
        audience: 'customer',
        answer: 'Two, both to the contract owner (or the customer admins if no owner is set): "contract expiring" when the end date is inside the reminder window (30 days unless the contract says otherwise), and "notice period opens" two weeks before the last day to serve notice. Each fires once; change the dates and it fires again for the new date.',
      },
      {
        question: 'How does Price Variance work?',
        audience: 'customer',
        answer: 'Each invoice line\'s unit price (net ÷ quantity) is compared with the pricebook rate for the same supplier and resource code, live on the invoice date. Over, exact or under — there is no tolerance. Lines with no live rate are counted as "no contract rate" but never compared. A monthly alert totals the lines priced above contract.',
      },
      {
        question: 'Can I see the contracts my customer holds with me?',
        audience: 'supplier',
        answer: 'Yes — Contracts in the sidebar lists the agreements and the pricebooks shared with you, read-only.',
      },
    ],
  },
  {
    slug: 'lead_times',
    category: 'Lead times and SLA',
    items: [
      {
        question: 'What is the lead-time register?',
        audience: 'customer',
        answer: 'The agreed number of days from call-off to delivery for each supplier — one default per supplier, with commodity-specific exceptions and effective dates. Lead Times in the sidebar. Two rows for the same supplier and commodity cannot overlap.',
      },
      {
        question: 'How does it change the supplier\'s SLA figure?',
        audience: 'customer',
        answer: 'When a monthly file arrives, each line\'s SLA date is calculated as the call-off date plus the lead time in force that day, instead of the SLA date the supplier typed into the file. Lines with no register entry, or no call-off date, keep the file\'s value. Delivery on or before the SLA date counts as within SLA.',
      },
      {
        question: 'When do I get an SLA alert?',
        audience: 'customer',
        answer: 'When a supplier\'s file scores below the SLA target (95% unless Admin Setup says otherwise). It goes to the admins on both sides and says how many lines were measured against the register.',
      },
      {
        question: 'Where do I see the lead times I am measured against?',
        audience: 'supplier',
        answer: 'My Lead Times in the sidebar — read-only. Your file\'s SLA date is calculated from these. If you have a commodity-specific lead time, add a Commodity_Code column to your Supplier Performance file so the right one is used.',
      },
    ],
  },
  {
    slug: 'audits',
    category: 'Audits and actions',
    items: [
      {
        question: 'How do I plan an audit?',
        audience: 'customer',
        answer: 'Audits → Plan audit: supplier, type (site, quality, process, health & safety, compliance), date, auditor and optional division. It appears on the Calendar and the auditor is reminded a week before. Record the outcome, score and findings on the audit when it is done.',
      },
      {
        question: 'What is the checklist questionnaire?',
        audience: 'customer',
        answer: 'Any questionnaire already assigned to the supplier can be attached to the audit as its checklist. Assign one from the supplier\'s Questionnaires tab first; the audit then links straight to the answers for review.',
      },
      {
        question: 'How do actions work?',
        audience: 'customer',
        answer: 'Raise an action from an audit, or a standalone one from the Actions tab: title, owner (a customer user), due date and priority. Open actions sit on the Calendar; one that passes its due date is marked overdue and its owner is told. Complete closes it. Cancelling an audit cancels its open actions.',
      },
      {
        question: 'What can I see of audits on my company?',
        audience: 'supplier',
        answer: 'Audits in the sidebar shows every audit your customer has raised on you, its outcome and findings, and the actions that came out of it — read-only. The actions are owned and closed on the customer side.',
      },
    ],
  },
  {
    slug: 'risk',
    category: 'Supplier risk',
    items: [
      {
        question: 'How do I assess a supplier\'s risk?',
        audience: 'customer',
        answer: 'Supplier profile → Risk tab → New assessment. Score each category — financial stability, supply continuity, quality, health & safety, legal & compliance, dependency, environmental — for likelihood and consequence, 1 to 5. Each cell gets a level; the overall score is the average out of 100, and the level follows it: low, medium, high or critical. Set a next review date.',
      },
      {
        question: 'What alerts come from risk?',
        audience: 'customer',
        answer: 'Saving an assessment at high or critical tells the assessor (or the admins); a supplier whose review date has passed raises a review-due reminder each day it is outstanding, once per date.',
      },
      {
        question: 'Where is the matrix?',
        audience: 'customer',
        answer: 'View matrix on the Risk tab shows the 5 × 5 grid with each category placed on it. Earlier assessments are kept as history under the latest one.',
      },
    ],
  },
  {
    slug: 'onboarding',
    category: 'Suppliers, onboarding and questionnaires',
    items: [
      {
        question: 'Where is the supplier directory?',
        audience: 'customer',
        answer: 'Suppliers. Approved, active suppliers are listed by default with their commodity approvals; open one for its performance, orders, contracts, risk, questionnaires, documents, calendar and users.',
      },
      {
        question: 'How do I bring on a new supplier?',
        audience: 'customer',
        answer: 'Supplier Onboarding → New invitation. The supplier registers, completes company details, uploads the compliance documents you require, answers the questionnaires and accepts the code of conduct, then submits. You review, request more information, approve or decline. Approved suppliers join the directory and the monthly upload cycle.',
      },
      {
        question: 'How do I assign a questionnaire?',
        audience: 'customer',
        answer: 'Questionnaires holds the templates (build or edit them there). Assign one from the supplier\'s Questionnaires tab, or to many suppliers at once from the template. Scored questionnaires show a percentage and pass or fail against the template\'s pass mark.',
      },
      {
        question: 'How do I complete a questionnaire?',
        audience: 'supplier',
        answer: 'Questionnaires in the sidebar lists what has been assigned to you. Answers save as you go; submit when every required question is answered. If your customer asks for more information, the questions they flagged reopen for you.',
      },
    ],
  },
  {
    slug: 'documents_communication',
    category: 'Documents and calendar',
    items: [
      {
        question: 'How are documents organised?',
        audience: 'both',
        answer: 'Documents has folders, with Customer documents and Supplier documents kept apart. A supplier\'s uploads file into a folder named after the supplier, and each side only sees folders that hold something shared with it. Archive keeps a document out of the lists without deleting it; Archived shows and restores them.',
      },
      {
        question: 'What appears on the Calendar?',
        audience: 'both',
        answer: 'Planned audits and open actions, contract end dates and business reviews, supplier approval expiries, and any event you add yourself. Click an audit or action to open it. On a supplier\'s profile the Calendar tab shows just that supplier\'s dates.',
      },
    ],
  },
  {
    slug: 'admin',
    category: 'Administration',
    items: [
      {
        question: 'Where are the switches for these modules?',
        audience: 'customer',
        answer: 'Admin Setup → Purchasing → Construction modules turns the monthly upload cycle, spend leakage, division scoring and the related pages on or off. Sidebar items and their order are under Admin Setup → Menu order; Helix administrators hold the platform-wide toggles, including the notification master switches, in Platform Admin.',
      },
      {
        question: 'How do I give a user access to a division or region?',
        audience: 'customer',
        answer: 'User Management → open the user → Site Access. Tick the divisions they may act for; the View Data For switcher then offers only those, and every page scopes to them.',
      },
    ],
  },
];
